import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/common/LanguageSelector';
import HomepageSlider from '../components/landing/HomepageSlider';
import { SmartCMRLogo } from '../components/brand/SmartCMRLogo';
import { 
  Truck, 
  FileText, 
  Download, 
  Users, 
  Shield, 
  Clock,
  Check,
  X,
  ArrowRight,
  Upload,
  PenLine,
  Send,
  AlertTriangle,
  FileX,
  Timer,
  Frown
} from 'lucide-react';

const LandingPage = () => {
  const { t } = useLanguage();

  const painPoints = [
    { icon: FileX, titleKey: 'pain1Title', descKey: 'pain1Desc' },
    { icon: AlertTriangle, titleKey: 'pain2Title', descKey: 'pain2Desc' },
    { icon: Timer, titleKey: 'pain3Title', descKey: 'pain3Desc' },
    { icon: Frown, titleKey: 'pain4Title', descKey: 'pain4Desc' },
  ];

  const solutions = [
    { titleKey: 'sol1Title', descKey: 'sol1Desc' },
    { titleKey: 'sol2Title', descKey: 'sol2Desc' },
    { titleKey: 'sol3Title', descKey: 'sol3Desc' },
    { titleKey: 'sol4Title', descKey: 'sol4Desc' },
  ];

  const howItWorksSteps = [
    {
      step: '1',
      titleKey: 'how1Title',
      descKey: 'how1Desc',
      mockup: '/images/mockup-cmr-upload.png',
      accent: '#F59E0B'
    },
    {
      step: '2',
      titleKey: 'how2Title',
      descKey: 'how2Desc',
      mockup: '/images/mockup-cmr-filling.png',
      accent: '#3B82F6'
    },
    {
      step: '3',
      titleKey: 'how3Title',
      descKey: 'how3Desc',
      mockup: '/images/mockup-cmr-form.png',
      accent: '#10B981'
    }
  ];

  const hp = (key) => t(`landing.homepage.${key}`);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center flex-shrink-0">
            <SmartCMRLogo />
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <LanguageSelector variant="ghost" showLabel={false} />
            <Link to="/contact" className="hidden md:block">
              <Button variant="ghost" size="sm" data-testid="contact-nav-btn" data-track-id="contact-nav-btn" data-track-label="Contact" data-track-category="navigation">{t('nav.contact')}</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm" className="px-2 sm:px-4" data-testid="login-nav-btn" data-track-id="login-nav-btn" data-track-label="Login" data-track-category="navigation">{t('nav.login')}</Button>
            </Link>
            <Link to="/register" className="hidden sm:block">
              <Button size="sm" className="px-2 sm:px-4" data-testid="register-nav-btn" data-track-id="register-nav-btn" data-track-label="Registreren" data-track-category="cta">{t('nav.register')}</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Slider */}
      <HomepageSlider />

      {/* Trust Bar */}
      <div className="bg-[#0B1F3B] border-t border-white/10" data-testid="trust-bar">
        <div className="container mx-auto px-4 py-4 sm:py-5">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 sm:gap-x-10 md:gap-x-14">
            {['trustBar1', 'trustBar2', 'trustBar3', 'trustBar4'].map((key, i) => (
              <div key={i} className="flex items-center gap-2 text-sm sm:text-base text-slate-300">
                <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-emerald-400" strokeWidth={3} />
                </div>
                <span>{hp(key)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pain Points Section */}
      <section className="py-16 sm:py-20 bg-white" data-testid="pain-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-14">
            <p className="text-sm font-semibold text-red-500 uppercase tracking-wider mb-3">{hp('painTag')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              {hp('painTitle')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto">
            {painPoints.map((pain, i) => (
              <div
                key={i}
                className="flex gap-4 p-5 sm:p-6 rounded-2xl bg-red-50/60 border border-red-100"
                data-testid={`pain-point-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                  <X className="w-5 h-5 text-red-500" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">{hp(pain.titleKey)}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{hp(pain.descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 sm:py-20 bg-slate-50" data-testid="solution-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-14">
            <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wider mb-3">{hp('solTag')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              {hp('solTitle')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto">
            {solutions.map((sol, i) => (
              <div
                key={i}
                className="flex gap-4 p-5 sm:p-6 rounded-2xl bg-emerald-50/60 border border-emerald-100"
                data-testid={`solution-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-emerald-600" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">{hp(sol.titleKey)}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{hp(sol.descKey)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Inline CTA */}
          <div className="text-center mt-10 sm:mt-12">
            <p className="text-sm sm:text-base font-medium text-slate-700 mb-5">{hp('solCtaText')}</p>
            <Link to="/register">
              <Button size="lg" className="rounded-full px-8 py-3 h-auto text-sm sm:text-base font-semibold bg-[#0B1F3B] hover:bg-[#162d4f] shadow-lg shadow-slate-900/20 transition-all hover:scale-[1.03] active:scale-[0.98]" data-testid="solution-cta" data-track-id="solution-cta" data-track-label="Start gratis (solutions)" data-track-category="cta">
                {hp('solCtaBtn')}
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
            </Link>
            <p className="mt-3 text-sm text-slate-500">{hp('solCtaSub')}</p>
            <p className="mt-1 text-xs text-slate-400">{hp('solCtaSub2')}</p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 sm:py-20 bg-white" data-testid="how-it-works-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">{hp('howTag')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              {hp('howTitle')}
            </h2>
            <p className="text-base sm:text-lg text-slate-500 mt-3 max-w-lg mx-auto">
              {hp('howSub')}
            </p>
          </div>

          <div className="flex flex-col gap-16 sm:gap-20 max-w-4xl mx-auto">
            {howItWorksSteps.map((item, i) => (
              <div
                key={i}
                className={`flex flex-col ${i % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 md:gap-12`}
                data-testid={`how-step-${i}`}
              >
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 -m-6 rounded-full opacity-[0.08] blur-[60px]" style={{ backgroundColor: item.accent }} />
                  <div className="relative w-[180px] sm:w-[200px] md:w-[220px] rounded-[28px] border-[5px] border-slate-700/80 bg-slate-900 shadow-2xl shadow-black/40 overflow-hidden ring-1 ring-white/10">
                    <img
                      src={item.mockup}
                      alt={hp(item.titleKey)}
                      className="w-full h-auto block -mt-[100px]"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div className="text-center md:text-left max-w-sm">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white font-bold text-lg mb-4" style={{ backgroundColor: item.accent }}>
                    {item.step}
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">{hp(item.titleKey)}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{hp(item.descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Authority Stats */}
      <section className="py-14 sm:py-16 bg-[#0B1F3B]" data-testid="trust-stats-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white tracking-tight">
              {hp('statsTitle')}
            </h2>
            <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-lg mx-auto">
              {hp('statsSub')}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 max-w-3xl mx-auto">
            {[
              { value: '60s', labelKey: 'stat1Label' },
              { value: '0', labelKey: 'stat2Label' },
              { value: '24/7', labelKey: 'stat3Label' },
              { value: '100%', labelKey: 'stat4Label' },
            ].map((stat, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <div className="text-3xl sm:text-4xl font-extrabold text-white mb-1">{stat.value}</div>
                <div className="text-xs sm:text-sm text-slate-400">{hp(stat.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 sm:py-20 bg-white" data-testid="social-proof-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-3">
              {hp('socialTitle')}
            </h2>
            <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
              {hp('socialSub')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mb-10">
            {['socialBlock1', 'socialBlock2', 'socialBlock3', 'socialBlock4'].map((key, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50/60 border border-emerald-100" data-testid={`trust-block-${i}`}>
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </div>
                <span className="text-sm sm:text-base font-medium text-slate-800">{hp(key)}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-10">
            {['socialTrigger1', 'socialTrigger2', 'socialTrigger3', 'socialTrigger4'].map((key, i) => (
              <span key={i} className="text-sm text-slate-500 flex items-center gap-1.5">
                <ArrowRight className="w-3 h-3 text-blue-500" />
                {hp(key)}
              </span>
            ))}
          </div>

          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 text-center mb-8">
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">
              {hp('socialBoostTitle')}
            </h3>
            <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto">
              {hp('socialBoostDesc')}
            </p>
          </div>

          <p className="text-center text-sm text-slate-400 mb-2">
            {hp('socialUrgency1')}
          </p>
          <p className="text-center text-xs text-slate-400 mb-8">
            {hp('socialUrgency2')}
          </p>

          <div className="text-center">
            <Link to="/register">
              <Button size="lg" className="rounded-full px-8 py-3 h-auto text-sm sm:text-base font-semibold bg-[#0B1F3B] hover:bg-[#162d4f] shadow-lg shadow-slate-900/20 transition-all hover:scale-[1.03] active:scale-[0.98]" data-testid="social-proof-cta" data-track-id="social-proof-cta" data-track-label="Start gratis (social proof)" data-track-category="cta">
                {hp('solCtaBtn')}
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
            </Link>
            <p className="mt-3 text-sm text-slate-500">{hp('solCtaSub')}</p>
            <p className="mt-1 text-xs text-slate-400">{hp('solCtaSub2')}</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              {hp('pricingTitle')}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.pricingDesc')}
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <Card className="border-blue-600 shadow-lg mb-8 relative">
              <CardHeader className="text-center pb-2 pt-6">
                <CardTitle className="text-2xl">SmartCMR</CardTitle>
                <CardDescription>{hp('planDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-6">
                  <span className="text-5xl font-bold text-slate-900">{t('landing.hc_euro1495')}</span>
                  <span className="text-slate-600"> {t('landing.perMonth')}</span>
                  <p className="text-sm text-slate-500 mt-1">{t('landing.exclVat')}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-left mb-6 max-w-md mx-auto">
                  {['planFeature1', 'planFeature2', 'planFeature3', 'planFeature4', 'planFeature5', 'planFeature6'].map((key, i) => (
                    <div key={i} className="flex items-start gap-2 text-slate-600 text-sm">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{hp(key)}</span>
                    </div>
                  ))}
                </div>
                <Link to="/register">
                  <Button className="w-full max-w-xs bg-blue-600 hover:bg-blue-700" data-testid="pricing-main-btn" data-track-id="pricing-main-btn" data-track-label="Start gratis (pricing)" data-track-category="cta">
                    {hp('pricingBtn')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="mt-2 text-xs text-slate-500">{hp('pricingAfter')}</p>
              </CardContent>
            </Card>
            <div className="grid md:grid-cols-1 gap-4 max-w-sm mx-auto">
              {[
                { nameKey: 'extraDriverName', priceKey: 'extraDriverPrice', periodKey: 'extraDriverPeriod', descKey: 'extraDriverDesc' },
              ].map((extra, index) => (
                <Card key={index} className="border-slate-200">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{hp(extra.nameKey)}</h3>
                        <p className="text-sm text-slate-500">{hp(extra.descKey)}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-slate-900">&euro;{hp(extra.priceKey)}</span>
                        <p className="text-xs text-slate-500">{hp(extra.periodKey)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Hard CTA Section */}
      <section className="py-16 sm:py-20 bg-[#0B1F3B] relative overflow-hidden" data-testid="hard-cta-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight mb-4">
            {hp('hardCtaTitle')}
          </h2>
          <p className="text-base sm:text-lg text-slate-300 max-w-xl mx-auto mb-8">
            {hp('hardCtaSub')}
          </p>
          <Link to="/register">
            <Button size="lg" className="rounded-full px-8 sm:px-10 py-3.5 h-auto text-base sm:text-lg font-semibold bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30 transition-all hover:scale-[1.03] active:scale-[0.98]" data-testid="hard-cta-btn" data-track-id="hard-cta-btn" data-track-label="Start nu gratis (hard CTA)" data-track-category="cta">
              {hp('hardCtaBtn')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="mt-3 text-sm text-slate-400">{hp('hardCtaSub1')}</p>
          <p className="mt-1 text-sm text-blue-400 font-medium">{hp('hardCtaSub2')}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 pb-24 sm:pb-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <SmartCMRLogo height={28} />
            </div>
            <div className="flex items-center gap-6">
              <Link to="/contact" className="text-sm text-slate-600 hover:text-slate-900 transition" data-track-id="footer-contact-link" data-track-label="Contact (footer)" data-track-category="navigation">
                {t('footer.contact.title')}
              </Link>
              <a href="mailto:info@smartcmr.nl" className="text-sm text-slate-600 hover:text-slate-900 transition" data-track-id="footer-email-link" data-track-label="Email (footer)" data-track-category="link">
                {t('footer.contact.email')}
              </a>
            </div>
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} SmartCMR. {t('landing.allRights')}
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile CTA — hidden when consent banner is visible (body.consent-banner-open) */}
      <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3" data-testid="sticky-mobile-cta" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <Link to="/register" className="block">
          <Button className="w-full h-12 rounded-full text-base font-semibold bg-[#0B1F3B] hover:bg-[#162d4f]" data-track-id="sticky-mobile-cta" data-track-label="Start gratis (sticky mobile)" data-track-category="cta">
            {hp('stickyBtn')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default LandingPage;
